/**
 * CSS colours as numbers for the graphics card. The theme gives colours in any CSS form (`#17d`,
 * `rgb(...)`, `oklch(...)`...), so the browser itself reads them: a pixel is painted with the
 * colour on a tiny canvas and read back. A theme has a dozen colours, so each is read once.
 */

/** A colour as red, green, blue and opacity, each from 0 to 1. */
export type Rgba = readonly [number, number, number, number];

/** Turns a CSS colour into {@link Rgba}. */
export type ColorReader = (css: string) => Rgba;

/** Fully transparent: what an unreadable colour becomes. */
export const TRANSPARENT: Rgba = [0, 0, 0, 0];

/** A reader that remembers every colour it read (`read` does the real work, once per colour). */
export function cachedColors(read: ColorReader): ColorReader {
  const known = new Map<string, Rgba>();
  return (css) => {
    let rgba = known.get(css);
    if (!rgba) {
      rgba = read(css);
      known.set(css, rgba);
    }
    return rgba;
  };
}

/** Reads colours with the page's own CSS engine (a 1×1 canvas). */
export function browserColors(): ColorReader {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 1;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  return cachedColors((css) => {
    if (!ctx || !css) return TRANSPARENT;
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = css;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
    return [r! / 255, g! / 255, b! / 255, a! / 255];
  });
}
